import {
  useCallback, useContext, useEffect, useRef, useState
} from 'react';

import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useVariationIds } from '@thd-olt-component-react/personalization-utils';

import { IMAGE_TYPES } from '../../constants';
import Helper from '../../component/Helper';
import { triggerAnalytics, getCurrentCampaignOptions } from './decisionEngineHelpers';
import { useImagePreload } from './useImagePreload';

// MAIN TEST SCENARIOS
// 1. EdgeWorker response available - valid SKU = DE image, no client-side call ✅
//   a. Click on valid SKU (in-scope to in-scope = DE image, no client-side call) ✅
//   b. Click on invalid SKU (in-scope to out-of-scope = default image, no client-side call) ✅
// 2. No EdgeWorker response or cached cookie - invalid SKU = default image, no client-side call ✅
//   a. Click on valid SKU (out-of-scope to in-scope = DE image, no client-side call) ✅
//   b. Click on invalid SKU (out-of-scope to out-of-scope = default image, no client-side call) ✅
// 3. No EdgeWorker response, but has a cached cookie - invalid SKU = default image, no client-side call ✅
//   a. Click on valid SKU (out-of-scope to in-scope = DE image, client-side call) ✅
//   b. Click on invalid SKU (out-of-scope to out-of-scope = default image, no client-side call) ✅
// 4. No EdgeWorker response - valid SKU (missing MCVIS ID) = default image, no client-side call ✅
//   a. Click on valid SKU (in-scope to in-scope = DE image, client-side call) ✅
//   b. Click on invalid SKU (in-scope to out-of-scope = default image, no client-side call) ✅
// 5. No EdgeWorker response - no campaign = default image, no client-side call ✅
//   a. Click on valid SKU (out-of-scope to out-of-scope = default image, no client-side call) ✅
//   b. Click on invalid SKU (out-of-scope to out-of-scope = default image, no client-side call) ✅
export const useDecisionEngine = ({ media, itemId, pageType = 'pip' }) => {
  /*
      decisionEngineCampaignOptions FS structure:
      {
        campaignId: {
          name: 'campaignName',
          requiresSKUList: true,
          requiresStrictEligibility: false,
          segments: ['segment1', 'segment2']
        }
      }
  */
  const decisionEngineCampaignOptions = useConfigService('fs:mediaGalleryDECampaignOptions');
  const enableDeFeature = useConfigService('fs:enableDeFeature');
  const {
    experiment: {
      decision_engine: deCampaignsFromEW
    } = {},
    segment
  } = useContext(ExperienceContext);
  const firstItemIdRef = useRef(itemId);
  const [superSkuEligibility, setSuperSkuEligibility] = useState('INVALID');
  const [deCallStatus, setDeCallStatus] = useState('NOT CALLED');

  // Get EdgeWorker response if available on the original page load SKU
  const currentDECampaign = Object.keys(deCampaignsFromEW || {})[0];
  const edgeWorkerDecision = deCampaignsFromEW?.[currentDECampaign];

  // Get client-side response from hook if available
  const {
    campaignId: storageKey = undefined,
    options: currentCampaignOptions = null
  } = getCurrentCampaignOptions({
    campaignOptions: decisionEngineCampaignOptions,
    segment
  });

  const { name: campaignName, requiresSKUList, requiresStrictEligibility } = currentCampaignOptions || {};
  const getSkuEligibility = useCallback(async () => {
    if (requiresSKUList && typeof window !== 'undefined') {
      const skuJSONModule = await import('./skuLists.json');
      const skuJSON = skuJSONModule.default;
      const segmentSkuList = Object.values(skuJSON).find((item) => item.segments.includes(segment))?.skuList || [];
      const segmentSkuSet = new Set(segmentSkuList);
      return segmentSkuSet.has(itemId);
    }
    if (requiresStrictEligibility) {
      const { images = [] } = media || {};
      return IMAGE_TYPES.every((type) => images.some((image) => image.type === type));
    }
    return true;
  }, [itemId, media]);

  useEffect(() => {
    let isMounted = true;

    // If the current page is in a campaign, check if the SKU is eligible
    if (currentCampaignOptions && itemId !== firstItemIdRef.current) {
      setSuperSkuEligibility('LOADING'); // Set to false while resolving

      // Need to run getSkuEligibility as a promise to dynamically import the skuLists.json file
      getSkuEligibility().then((result) => {
        if (isMounted) {
          const resultString = result === true ? 'VALID' : 'INVALID';
          setSuperSkuEligibility(resultString);
        }
      }).catch(() => {
        if (isMounted) {
          // If there was an error, it would be when the campaign has some SKU eligibility requirements
          // So if an error occurs, we set the eligibility to INVALID to be safe
          setSuperSkuEligibility('INVALID');
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [getSkuEligibility]);

  const threeHourExpiration = new Date(new Date().setHours(new Date().getHours() + 3)).getTime() - Date.now();
  const shouldMakeDECall = deCallStatus === 'LOADING' // ensures that DE call won't get re-triggered while loading
    || (enableDeFeature // DE feature is enabled
      && superSkuEligibility === 'VALID' // SKU is eligible
      && !edgeWorkerDecision && itemId !== firstItemIdRef.current); // No EW response and not the original page load SKU
    // && deCallStatus === 'NOT CALLED'; // DE call has not been made yet
  if (shouldMakeDECall && deCallStatus === 'NOT CALLED') {
    setDeCallStatus('LOADING');
  }
  const variationIdResponse = useVariationIds({
    campaignIds: [{ campaignId: campaignName }],
    storageOpts: {
      key: storageKey,
      isCookieStorage: true,
      expiration: threeHourExpiration
    },
    // Run the DE call only if these are all true
    // 1. the DE feature is enabled
    // 2. there is no EW response,
    // 3. the SKU is eligible
    // 4. the SKU is not the original page load SKU
    isReady: !!shouldMakeDECall
  });
  const clientDecision = variationIdResponse?.variationIds?.[0]?.decision || null;

  // Determine which decision to use
  let decision;
  if (edgeWorkerDecision && (firstItemIdRef.current === itemId || superSkuEligibility === 'VALID')) {
    // Use the EW decision if we are on the original page load SKU
    // OR the SKU is eligible for the campaign. This second condition is to prevent duplicate analytics
    decision = edgeWorkerDecision;
  } else if (clientDecision && superSkuEligibility === 'VALID') {
    // Use the client-side decision if the SKU is eligible
    // and there is no EW response
    // and the SKU is not the original page load SKU
    decision = clientDecision;
  } else if (
    (superSkuEligibility === 'LOADING' && firstItemIdRef.current !== itemId)
    || (!clientDecision && deCallStatus === 'LOADING')
  ) {
    // SuperSku eligibility or DE call is loading
    decision = 'LOADING';
  } else if (currentCampaignOptions && superSkuEligibility === 'INVALID') {
    decision = IMAGE_TYPES[0];
  }

  // If the DE feature is enabled and there is a Decision Engine response,
  const isDecisionEngineEligible = enableDeFeature && decision;
  const imageType = Helper.armToImageMapping({ decision });

  // Only runs/works on server-side
  useImagePreload({
    enableDeFeature,
    imageType,
    media,
    pageType,
  });

  useEffect(() => {
    // This is to trigger the one-time analytics event for the decision engine
    // when the page loads, we have a decision from the EdgeWorker, and the decision engine is enabled
    triggerAnalytics({ currentDECampaign, enableDeFeature });

    window.LIFE_CYCLE_EVENT_BUS.on('decision-engine.api-call', ({ output }) => {
      const { loading, error } = output;

      if (error) {
        setDeCallStatus('ERROR');
      } else if (!loading) {
        setDeCallStatus('COMPLETE');
      }
    });
  }, []);

  return { imageType: isDecisionEngineEligible ? imageType : null, loading: decision === 'LOADING' };
};